import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { RagBadge } from "@/components/common/RagBadge";
import { InviteLinkResult } from "@/components/common/InviteLinkResult";
import { fmtRelative } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const AMDEN_ROLES = [
  { value: "amden_consultant", label: "Amden Consultant" },
  { value: "amden_super_admin", label: "Amden Super Administrator" },
];
const ROLE_RAG = { amden_super_admin: "red", amden_consultant: "blue" };

const RAG_SNAP = [
  { key: "healthy", label: "Healthy", cls: "text-[#3D8B65]", dot: "bg-[#3D8B65]" },
  { key: "monitor", label: "Monitor", cls: "text-[#4A78A8]", dot: "bg-[#4A78A8]" },
  { key: "attention", label: "Attention", cls: "text-[#B87F1E]", dot: "bg-[#D99A32]" },
  { key: "high_risk", label: "High risk", cls: "text-[#C95555]", dot: "bg-[#C95555]" },
];

export default function AmdenTeam() {
  const { user, isSuper } = useAuth();
  const [staff, setStaff] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [reassignTo, setReassignTo] = useState("none");

  const load = () => api.get("/amden-team").then((r) => setStaff(r.data)).catch(() => setStaff([]));
  useEffect(() => { if (isSuper) load(); }, [isSuper]);

  useEffect(() => {
    if (!deleteTarget) { setWorkload(null); setReassignTo("none"); return; }
    setWorkload(null); setReassignTo("none");
    api.get(`/users/${deleteTarget.id}/workload`).then((r) => setWorkload(r.data)).catch(() => setWorkload(null));
  }, [deleteTarget]);

  if (!isSuper) return <EmptyState icon="ShieldAlert" title="Not permitted" description="Only Amden Super Administrators can manage the Amden team." />;
  if (!staff) return <PageLoading />;

  const deleteUser = async () => {
    try {
      const q = reassignTo !== "none" ? `?reassign_to=${reassignTo}` : "";
      await api.delete(`/users/${deleteTarget.id}/permanent${q}`);
      toast.success("Team member removed");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="amden-team-page">
      <PageHeader
        eyebrow="Amden"
        title="Amden Team"
        subtitle="Add and manage your Amden consultants and administrators, and assign consultants to clients."
        icon="ShieldCheck"
        actions={<Button onClick={() => setAdding(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="add-amden-staff-btn"><Icons.UserPlus size={16} className="mr-1.5" /> Add Amden staff</Button>}
      />

      {staff.some((u) => u.role === "amden_consultant") && (
        <div className="mb-6" data-testid="consultant-snapshot">
          <h3 className="mb-3 font-display text-sm font-bold text-[#0B1729]">Consultant snapshot</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {staff.filter((u) => u.role === "amden_consultant").map((u) => {
              const r = u.client_rag || { total: 0 };
              return (
                <div key={u.id} className="amden-card p-4" data-testid={`consultant-snapshot-${u.id}`}>
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0B1729] text-xs font-bold text-[#FFC24D]">{u.name?.[0]}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-[#0B1729]">{u.name}</div>
                      <div className="text-xs text-[#1E2D44]/50">{r.total} client{r.total === 1 ? "" : "s"}</div>
                    </div>
                  </div>
                  {r.total === 0 ? (
                    <p className="mt-3 text-xs text-[#1E2D44]/40">No clients assigned yet.</p>
                  ) : (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {RAG_SNAP.map((s) => (
                        <div key={s.key} className="flex items-center gap-1.5 rounded-lg bg-[#F4F6F9] px-2.5 py-1.5" data-testid={`snapshot-${u.id}-${s.key}`}>
                          <span className={`h-2 w-2 shrink-0 rounded-full ${s.dot}`} />
                          <span className="text-xs text-[#1E2D44]/60">{s.label}</span>
                          <span className={`ml-auto text-sm font-bold ${s.cls}`}>{r[s.key] || 0}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {staff.length === 0 ? (
        <EmptyState icon="ShieldCheck" title="No Amden staff yet" description="Add your first consultant or administrator." />
      ) : (
        <div className="amden-card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#E4E8EF] text-left text-xs uppercase tracking-wide text-[#1E2D44]/45">
                <th className="px-6 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Assigned clients</th>
                <th className="px-4 py-3 font-semibold">Last active</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {staff.map((u) => (
                  <tr key={u.id} className="border-b border-[#EEF1F6]" data-testid={`amden-row-${u.id}`}>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0B1729] text-xs font-bold text-[#FFC24D]">{u.name?.[0]}</span>
                        <div><div className="font-medium text-[#0B1729]">{u.name}{u.id === user?.id && <span className="ml-1.5 text-[11px] text-[#1E2D44]/40">(you)</span>}</div><div className="text-xs text-[#1E2D44]/50">{u.email}</div></div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5"><RagBadge rag={ROLE_RAG[u.role]}>{u.role_label}</RagBadge></td>
                    <td className="px-4 py-3.5 text-[#1E2D44]/70">{u.title || "—"}</td>
                    <td className="px-4 py-3.5">
                      {u.role === "amden_consultant"
                        ? <button onClick={() => setManaging(u)} className="text-sm font-medium text-[#3E6DB5] hover:underline" data-testid={`manage-clients-${u.id}`}>{u.assigned_client_count} client{u.assigned_client_count === 1 ? "" : "s"}</button>
                        : <span className="text-[#1E2D44]/40">All clients</span>}
                    </td>
                    <td className="px-4 py-3.5" data-testid={`amden-activity-${u.id}`}>
                      <div className={`text-sm font-medium ${u.last_active ? "text-[#0B1729]" : "text-[#1E2D44]/40"}`}>{fmtRelative(u.last_active)}</div>
                      <div className="text-[11px] text-[#1E2D44]/50">
                        {u.recent_activity_count > 0 ? `${u.recent_activity_count} action${u.recent_activity_count === 1 ? "" : "s"} this week` : "No activity this week"}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">{u.active ? <RagBadge rag="green">Active</RagBadge> : <RagBadge rag="grey">Inactive</RagBadge>}</td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditing(u)} data-testid={`edit-amden-${u.id}`}><Icons.Pencil size={14} /></Button>
                        {u.id !== user?.id && <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(u)} className="text-[#C95555] hover:bg-[#C95555]/10 hover:text-[#b34848]" data-testid={`delete-amden-${u.id}`}><Icons.Trash2 size={14} /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {adding && <AddDialog onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {editing && <EditDialog u={editing} isSelf={editing.id === user?.id} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {managing && <ManageClientsDialog u={managing} onClose={() => setManaging(null)} onSaved={() => { setManaging(null); load(); }} />}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-amden-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this team member?</AlertDialogTitle>
            <AlertDialogDescription>{deleteTarget?.name} ({deleteTarget?.email}) will be permanently removed and can no longer sign in. Any clients they lead will be left without an assigned consultant. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          {workload?.open_actions > 0 && (
            <div className="rounded-lg border border-[#FFB000]/40 bg-[#FFB000]/8 p-3">
              <p className="text-sm text-[#1E2D44]/80"><span className="font-semibold text-[#0B1729]">{workload.open_actions}</span> open action(s) are owned by this person.</p>
              <label className="mt-2 block text-xs font-medium text-[#1E2D44]/70">Reassign their open actions to</label>
              <Select value={reassignTo} onValueChange={setReassignTo}>
                <SelectTrigger className="mt-1 bg-white" data-testid="amden-reassign-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Leave unassigned</SelectItem>
                  {(workload.candidates || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.role_label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteUser} className="bg-[#C95555] hover:bg-[#b34848]" data-testid="confirm-delete-amden">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AddDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", role: "amden_consultant", title: "", financial_access: true });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const save = async () => {
    if (!form.name || !form.email) return toast.error("Add a name and email");
    setSaving(true);
    try { const res = await api.post("/users", form); toast.success("Amden staff added"); setResult({ ...res.data, name: form.name }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="add-amden-dialog">
        <DialogHeader>
          <DialogTitle>{result ? "Invite sent" : "Add Amden staff"}</DialogTitle>
          <DialogDescription>{result ? "The new team member can now set their own password." : "Create a new Amden consultant or administrator. They'll receive a secure email link to set their own password."}</DialogDescription>
        </DialogHeader>
        {result ? (
          <>
            <InviteLinkResult name={result.name} result={result} />
            <DialogFooter>
              <Button onClick={onSaved} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="amden-invite-done">Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="amden-name" /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="amden-email" placeholder="name@amden.ie" /></div>
              </div>
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" placeholder="e.g. Business Consultant" /></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1" data-testid="amden-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{AMDEN_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <p className="flex items-center gap-1.5 text-xs text-[#1E2D44]/45"><Icons.Mail size={13} /> A secure set-password link will be emailed to them.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-amden-btn">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Add staff"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ u, isSelf, onClose, onSaved }) {
  const [name, setName] = useState(u.name || "");
  const [email, setEmail] = useState(u.email || "");
  const [role, setRole] = useState(u.role);
  const [title, setTitle] = useState(u.title || "");
  const [active, setActive] = useState(u.active);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const save = async () => {
    if (!name.trim()) return toast.error("Name can't be empty");
    if (!email.trim()) return toast.error("Email can't be empty");
    if (password && password.length < 8) return toast.error("Password must be at least 8 characters");
    setSaving(true);
    const payload = { name: name.trim(), email: email.trim(), title };
    if (!isSelf) { payload.role = role; payload.active = active; }
    if (password) payload.password = password;
    try {
      await api.patch(`/users/${u.id}`, payload);
      toast.success(password ? "Team member updated & password set" : "Team member updated");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const resend = async () => {
    setResending(true);
    try {
      const res = await api.post(`/users/${u.id}/resend-invite`);
      toast.success(res.data.email_sent ? "Set-password link emailed" : "Link generated — email delivery not configured");
      if (!res.data.email_sent && res.data.invite_link) {
        try { await navigator.clipboard.writeText(res.data.invite_link); toast.success("Link copied to clipboard"); } catch {}
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setResending(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-amden-dialog">
        <DialogHeader>
          <DialogTitle>Edit {u.name}</DialogTitle>
          <DialogDescription>{isSelf ? "Update your own name, email, title and password. Role and status changes must be made by another administrator." : "Update this team member's details, role, status and password."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="edit-amden-name" /></div>
            <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="edit-amden-email" /></div>
          </div>
          <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="edit-amden-title" /></div>
          <div><Label>Role</Label>
            <Select value={role} onValueChange={setRole} disabled={isSelf}>
              <SelectTrigger className="mt-1" data-testid="edit-amden-role"><SelectValue /></SelectTrigger>
              <SelectContent>{AMDEN_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-[#E4E8EF] p-3">
            <div className="text-sm font-medium">Active</div>
            <Switch checked={active} onCheckedChange={setActive} disabled={isSelf} data-testid="edit-amden-active" />
          </div>
          <div className="rounded-lg border border-[#E4E8EF] p-3">
            <Label className="text-sm">Set a new password</Label>
            <div className="relative mt-1">
              <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank to keep unchanged" className="pr-9" data-testid="edit-amden-password" />
              <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#1E2D44]/45 hover:text-[#0B1729]" data-testid="toggle-amden-password">
                {showPw ? <Icons.EyeOff size={15} /> : <Icons.Eye size={15} />}
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-[#1E2D44]/45">Minimum 8 characters. Setting a password here signs the user in immediately with it and cancels any pending set-password link.</p>
          </div>
          {!isSelf && active && (
            <button onClick={resend} disabled={resending} className="flex items-center gap-1.5 text-xs font-medium text-[#B87F1E] hover:underline disabled:opacity-50" data-testid="resend-invite-amden">
              {resending ? <Icons.Loader2 className="animate-spin" size={13} /> : <Icons.MailPlus size={13} />} Resend set-password link instead
            </button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-edit-amden">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageClientsDialog({ u, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/amden-team/${u.id}/clients`).then((r) => { setData(r.data); setSelected(new Set(r.data.assigned)); });
  }, [u.id]);
  const toggle = (id) => {
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const save = async () => {
    setSaving(true);
    try { await api.put(`/amden-team/${u.id}/clients`, { organisation_ids: Array.from(selected) }); toast.success("Client assignments updated"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="manage-clients-dialog">
        <DialogHeader>
          <DialogTitle>Assign clients — {u.name}</DialogTitle>
          <DialogDescription>Select the client organisations this consultant leads. Assigning here sets them as the client's consultant.</DialogDescription>
        </DialogHeader>
        {!data ? <PageLoading /> : data.candidates.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#1E2D44]/50">No client organisations yet.</p>
        ) : (
          <div className="max-h-[360px] space-y-1 overflow-y-auto">
            {data.candidates.map((c) => (
              <label key={c.id} className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-[#F4F6F9]" data-testid={`client-option-${c.id}`}>
                <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#0B1729]">{c.company_name}</div>
                  {c.current_consultant && !selected.has(c.id) && <div className="text-[11px] text-[#1E2D44]/45">Currently led by {c.current_consultant}</div>}
                  {c.current_consultant && selected.has(c.id) && <div className="text-[11px] text-[#B87F1E]">Will move from {c.current_consultant}</div>}
                </div>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !data} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-clients-btn">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Save assignments"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
